import { Alert, Box, Typography } from "@mui/material";
import { cloneElement, ComponentProps, ReactElement, useState } from "react";

import { FlexBox } from "~frontend/components/FlexBox";
import { generatedBy } from "~frontend/components/helpers/stringFormattingHelpers";
import theme, { palette } from "~frontend/theme";

import { CampaignExportButton } from "./CampaignExport";
import { CampaignViewAccordion } from "./CampaignViewAccordion";
import { CampaignForView } from "./types";

export function ExportsRow({
  header,
  bodyText,
  button,
}: {
  header: string;
  bodyText: string;
  button: ReactElement<typeof CampaignExportButton>;
}): ReactElement {
  const refreshLink = cloneElement(button, {
    buttonType: "REFRESH_LINK",
  } as Partial<ComponentProps<typeof CampaignExportButton>>);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const rowButton = cloneElement(button, {
    setErrorMessage: setErrorMessage,
  } as Partial<ComponentProps<typeof CampaignExportButton>>);

  return (
    <Box
      sx={{
        backgroundColor: palette.lightestGray,
        padding: 3,
      }}
    >
      <FlexBox justifyContent="space-between" alignItems="center" gap={1}>
        <Box flex={4}>
          <Typography variant="body1Bold">{header}</Typography>
          <Typography variant="body1">
            {bodyText} {refreshLink}
          </Typography>
        </Box>
        <Box flex={1}>
          <Box sx={{ float: "right", textWrap: "nowrap" }}>{rowButton}</Box>
        </Box>
      </FlexBox>
      {errorMessage && (
        <Box>
          <Alert severity="error" sx={{ marginTop: 1 }}>
            {errorMessage}
          </Alert>
        </Box>
      )}
    </Box>
  );
}

export function Exports({
  campaign,
  expanded,
  onExportChange,
}: {
  campaign: CampaignForView;
  expanded?: boolean;
  onExportChange?: () => void;
}): ReactElement {
  return (
    <CampaignViewAccordion
      header="Export Campaign Data"
      sx={{ backgroundColor: palette.white, mt: 0 }}
      expanded={expanded}
      onAccordionChange={onExportChange}
    >
      <FlexBox gap={theme.spacing(2)} direction={"column"}>
        <ExportsRow
          header={
            campaign.campaignExportJob
              ? `Campaign Results Export of ${campaign.name}`
              : "Campaign Results"
          }
          bodyText={
            campaign.campaignExportJob
              ? generatedBy(
                  campaign.campaignExportJob.args.requestedBy.fullName,
                  campaign.campaignExportJob.createdAt
                )
              : "Generate an export of your campaign results, including reply status, shortlink clicks, opt out status, tags, and donations."
          }
          button={
            <CampaignExportButton campaign={campaign} exportType="RESULTS" />
          }
        />
        <ExportsRow
          header={
            campaign.campaignRepliesExportJob
              ? `Campaign Replies Export of ${campaign.name}`
              : "Campaign Replies"
          }
          bodyText={
            campaign.campaignRepliesExportJob
              ? generatedBy(
                  campaign.campaignRepliesExportJob.args.requestedBy.fullName,
                  campaign.campaignRepliesExportJob.createdAt
                )
              : "Generate an export of your campaign's inbound and outbound replies."
          }
          button={
            <CampaignExportButton campaign={campaign} exportType="REPLIES" />
          }
        />
      </FlexBox>
    </CampaignViewAccordion>
  );
}
